import _ from 'lodash';
import type { z } from 'zod';
import { decodeClassFromDb, encodeClassToDb } from '../classes/codecs';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBStudentActivityLogSchema, StudentActivityLogSchema } from './schemas';

type DBStudentActivityLog = z.infer<typeof DBStudentActivityLogSchema>;
type StudentActivityLog = z.infer<typeof StudentActivityLogSchema>;

export const StudentActivityLogCodecConfig: TransformerConfig<
  typeof StudentActivityLogSchema,
  typeof DBStudentActivityLogSchema
> = {
  name: 'StudentActivityLog',
  clientSchema: StudentActivityLogSchema,
  dbSchema: DBStudentActivityLogSchema,
  transformers: [
    // activityType + creationDatetime → typeDatetimeIdentifier: 'activityType#creationDatetime'
    // Note: activityType only exists in client schema, creationDatetime exists in both
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['activityType', 'creationDatetime'] as const,
        dependencies: ['activityType', 'creationDatetime'] as const,
        encode: ({ activityType, creationDatetime }) => ({
          typeDatetimeIdentifier: `${activityType}#${creationDatetime}`,
          creationDatetime, // Only pass through fields that exist in DB schema
        }),
      },
      decoder: {
        sourceFields: ['typeDatetimeIdentifier'] as const,
        decode: ({ typeDatetimeIdentifier }) => {
          const [activityType, creationDatetime] = (
            typeDatetimeIdentifier as DBStudentActivityLog['typeDatetimeIdentifier']
          ).split('#');
          return {
            activityType: activityType as StudentActivityLog['activityType'],
            creationDatetime: Number(creationDatetime),
          };
        },
      },
    }),
    // class: nested transformation for sort keys and students. Legacy records
    // pre-date the class snapshot being written on every log entry, so the
    // field may be absent — short-circuit both directions in that case.
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['class'] as const,
        encode: ({ class: classData }) => {
          if (classData === undefined) return {};
          return {
            class: encodeClassToDb({
              data: classData as NonNullable<StudentActivityLog['class']>,
            }),
          };
        },
      },
      decoder: {
        sourceFields: ['class'] as const,
        decode: ({ class: classData }) => {
          if (classData === undefined) return {};
          return {
            class: decodeClassFromDb({
              data: classData as NonNullable<DBStudentActivityLog['class']>,
            }),
          };
        },
      },
    }),
    // recentAttendedLessons: optional nested array transformation
    createTransformer<typeof StudentActivityLogSchema, typeof DBStudentActivityLogSchema>({
      encoder: {
        sourceFields: ['recentAttendedLessons'] as const,
        encode: ({ recentAttendedLessons }) => {
          if (!recentAttendedLessons) return {};
          const lessons = recentAttendedLessons as NonNullable<
            StudentActivityLog['recentAttendedLessons']
          >;
          return {
            recentAttendedLessons: lessons.map(enrolment => ({
              ..._.omit(enrolment, ['classId', 'lesson', 'rollMarked']),
              classLessonIdentifier: `${enrolment.classId.subject}#${enrolment.classId.classId}#${enrolment.lesson.course}#${enrolment.lesson.lessonId}#${enrolment.lesson.counter}`,
              rollMarked: enrolment.rollMarked ? 'true' : 'false',
            })),
          };
        },
      },
      decoder: {
        sourceFields: ['recentAttendedLessons'] as const,
        decode: ({ recentAttendedLessons }) => {
          if (!recentAttendedLessons) return {};
          const lessons = recentAttendedLessons as NonNullable<
            DBStudentActivityLog['recentAttendedLessons']
          >;
          type ClientLesson = NonNullable<StudentActivityLog['recentAttendedLessons']>[number];
          return {
            recentAttendedLessons: lessons.map(enrolment => {
              const parts = enrolment.classLessonIdentifier.split('#');
              return {
                ..._.omit(enrolment, ['classLessonIdentifier', 'rollMarked']),
                classId: {
                  subject: parts[0] as ClientLesson['classId']['subject'],
                  classId: parts[1]!,
                },
                lesson: {
                  course: parts[2]!,
                  lessonId: parts[3]!,
                  counter: parts[4]!,
                },
                rollMarked: enrolment.rollMarked === 'true',
              };
            }),
          };
        },
      },
    }),
  ],
};

export const encodeStudentActivityLogToDb = createEncodeToDbFn(StudentActivityLogCodecConfig);
export const decodeStudentActivityLogFromDb = createDecodeFromDbFn(StudentActivityLogCodecConfig);
